import React from 'react';
import { Button } from '../../components/ui/button';
import {
  UtensilsCrossed, CreditCard, Banknote, CheckCircle, Clock, ChefHat,
  StickyNote, X, Percent, Cake, Gift, CalendarDays, Star,
  TrendingUp, ShoppingBag, DollarSign, BarChart3, Tag, Radio,
  QrCode, Building2
} from 'lucide-react';
import { KitchenPrintButton } from './KitchenPrintButton';

const statusConfig = {
  received: { color: 'bg-[#E9C46A] text-[#2D241D]', icon: Clock },
  preparing: { color: 'bg-[#E16833] text-white', icon: ChefHat },
  ready: { color: 'bg-[#2D6A4F] text-white', icon: UtensilsCrossed },
  served: { color: 'bg-[#0A422D] text-white', icon: CheckCircle }
};

export const OrdersTab = ({
  orders, analytics, reservations, birthdays, adminRole,
  updateOrderStatus, closeOrder, formatPrice, formatDate,
  onCashPayment, onCbPayment, onOtherPayment, onDiscount, customers,
}) => {
  return (
    <>
      {/* Analytics Dashboard */}
      {analytics && (
        <div className="mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-revenue">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#0A422D]/10 flex items-center justify-center"><DollarSign className="w-4 h-4 text-[#0A422D]" /></div>
                <span className="text-xs text-[#6E5E52]">Chiffre d'affaires</span>
              </div>
              <p className="text-xl font-bold text-[#0A422D]">{formatPrice(analytics.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-orders">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#E16833]/10 flex items-center justify-center"><ShoppingBag className="w-4 h-4 text-[#E16833]" /></div>
                <span className="text-xs text-[#6E5E52]">Commandes</span>
              </div>
              <p className="text-xl font-bold text-[#E16833]">{analytics.total_orders}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-basket">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#D4A853]/10 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-[#D4A853]" /></div>
                <span className="text-xs text-[#6E5E52]">Panier moyen</span>
              </div>
              <p className="text-xl font-bold text-[#D4A853]">{formatPrice(analytics.avg_basket)}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-tips">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center"><Gift className="w-4 h-4 text-[#2D6A4F]" /></div>
                <span className="text-xs text-[#6E5E52]">Pourboires</span>
              </div>
              <p className="text-xl font-bold text-[#2D6A4F]">{formatPrice(analytics.total_tips)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-cash">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center"><Banknote className="w-4 h-4 text-[#2D6A4F]" /></div>
                <span className="text-xs text-[#6E5E52]">Cash</span>
              </div>
              <p className="text-lg font-bold text-[#2D6A4F]">{formatPrice(analytics.cash_revenue || 0)}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-stripe">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#6366F1]/10 flex items-center justify-center"><CreditCard className="w-4 h-4 text-[#6366F1]" /></div>
                <span className="text-xs text-[#6E5E52]">CB / Stripe</span>
              </div>
              <p className="text-lg font-bold text-[#6366F1]">{formatPrice((analytics.card_terminal_revenue || 0) + (analytics.stripe_revenue || 0))}</p>
            </div>
            {adminRole === 'admin' && (
              <>
                <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-margin">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-[#D4A853]/10 flex items-center justify-center"><Percent className="w-4 h-4 text-[#D4A853]" /></div>
                    <span className="text-xs text-[#6E5E52]">Marge</span>
                  </div>
                  <p className="text-lg font-bold text-[#D4A853]">{analytics.margin_percent || 0}%</p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="stat-discounts">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-[#E63946]/10 flex items-center justify-center"><Tag className="w-4 h-4 text-[#E63946]" /></div>
                    <span className="text-xs text-[#6E5E52]">Remises</span>
                  </div>
                  <p className="text-lg font-bold text-[#E63946]">{formatPrice(analytics.total_discounts || 0)}</p>
                </div>
              </>
            )}
          </div>

          {analytics.top_dishes?.length > 0 && (
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4">
              <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#E16833]" /> Top 5 plats</h3>
              <div className="space-y-2">
                {analytics.top_dishes.slice(0, 5).map((dish, idx) => {
                  const maxCount = analytics.top_dishes[0]?.count || 1;
                  return (
                    <div key={idx} className="flex items-center gap-3" data-testid={`top-dish-${idx}`}>
                      <span className="w-5 h-5 rounded-full bg-[#F3EDE5] flex items-center justify-center text-[10px] font-bold text-[#6E5E52]">{idx + 1}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#2D241D] font-medium truncate">{dish.name}</span>
                          <span className="text-[#E16833] font-bold ml-2">{dish.count}</span>
                        </div>
                        <div className="w-full bg-[#F3EDE5] rounded-full h-1.5 mt-1">
                          <div className="bg-gradient-to-r from-[#D4A853] to-[#E16833] h-1.5 rounded-full" style={{ width: `${(dish.count / maxCount) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {analytics.hours_data?.length > 0 && (
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
              <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#D4A853]" /> Heures de pointe</h3>
              <div className="flex items-end gap-1 h-20">
                {analytics.hours_data.map((h, idx) => {
                  const maxOrders = Math.max(...analytics.hours_data.map(d => d.orders), 1);
                  const pct = (h.orders / maxOrders) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-0.5" data-testid={`hour-bar-${h.hour}`}>
                      <div className="w-full rounded-t" style={{ height: `${Math.max(pct, 4)}%`, background: pct > 60 ? '#E16833' : pct > 30 ? '#D4A853' : '#E5DCD3' }} />
                      <span className="text-[8px] text-[#6E5E52]">{h.hour}h</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upcoming reservations */}
      {reservations.filter(r => r.status === 'pending' || r.status === 'confirmed').length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4">
          <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#0A422D]" /> Reservations a venir ({reservations.filter(r => r.status === 'pending' || r.status === 'confirmed').length})
          </h3>
          <div className="space-y-2">
            {reservations.filter(r => r.status === 'pending' || r.status === 'confirmed').slice(0, 5).map(res => (
              <div key={res.id} className="flex items-center justify-between bg-[#F3EDE5] rounded-xl px-3 py-2" data-testid={`dash-res-${res.id}`}>
                <div>
                  <p className="text-sm font-medium text-[#2D241D]">{res.name}</p>
                  <p className="text-xs text-[#6E5E52]">{res.date} a {res.time} - {res.guests} pers.</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${res.status === 'confirmed' ? 'bg-[#2D6A4F] text-white' : 'bg-[#E9C46A] text-[#2D241D]'}`}>
                  {res.status === 'confirmed' ? 'Confirme' : 'En attente'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Birthdays */}
      {birthdays.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4">
          <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
            <Cake className="w-4 h-4 text-[#E16833]" /> Anniversaires cette semaine ({birthdays.length})
          </h3>
          <div className="space-y-2">
            {birthdays.map(c => (
              <div key={c.id} className="flex items-center justify-between bg-[#FFF3E0] rounded-xl px-3 py-2" data-testid={`birthday-${c.id}`}>
                <div>
                  <p className="text-sm font-medium text-[#2D241D]">{c.name}</p>
                  <p className="text-xs text-[#6E5E52]">{c.birthday_display} — {c.email}
                    {c.days_until === 0 ? <span className="ml-1 text-[#E16833] font-bold">Aujourd'hui !</span>
                      : <span className="ml-1">dans {c.days_until} jour{c.days_until > 1 ? 's' : ''}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="font-semibold text-[#2D241D] mb-3">Commandes en cours</h3>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {orders.filter(o => !o.is_fully_paid).map(order => {
          const StatusIcon = statusConfig[order.status]?.icon || Clock;
          const afterDiscount = order.total - (order.discount_amount || 0);
          const remaining = afterDiscount - (order.paid_amount || 0);
          return (
            <div key={order.id} className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid={`order-card-${order.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-[#2D241D]">Table {order.table_id}</p>
                    {order.order_number && (
                      <span className="px-2 py-0.5 rounded-md bg-[#1B3A4B] text-white text-[10px] font-bold tracking-wider" data-testid={`order-number-${order.id}`}>{order.order_number}</span>
                    )}
                    {order.grab_order_id && (() => {
                      const pColors = {
                        grab: '#00B14F', foodpanda: '#D70F64', line_man: '#00C300',
                        uber_eats: '#222', deliveroo: '#00CCBC', just_eat: '#FF8000',
                        glovo: '#FFC244', doordash: '#FF3008', talabat: '#FF5800',
                        wolt: '#009DE0', bolt_food: '#34D186', careem: '#49B44D',
                        custom_delivery: '#8B5CF6',
                      };
                      const pNames = {
                        grab: 'GRAB', uber_eats: 'UBER EATS', deliveroo: 'DELIVEROO',
                        just_eat: 'JUST EAT', glovo: 'GLOVO', doordash: 'DOORDASH',
                        foodpanda: 'FOODPANDA', line_man: 'LINE MAN', talabat: 'TALABAT',
                        wolt: 'WOLT', bolt_food: 'BOLT', careem: 'CAREEM',
                        custom_delivery: 'LIVRAISON',
                      };
                      const src = order.order_source;
                      const c = pColors[src] || '#00B14F';
                      const n = pNames[src] || src?.toUpperCase() || 'GRAB';
                      return (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold"
                          style={{ backgroundColor: `${c}15`, color: c }}>
                          {n} #{order.grab_order_id}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-[#6E5E52]">{formatDate(order.created_at)}{order.server_name ? ` - ${order.server_name}` : ''}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig[order.status]?.color}`}>
                  <StatusIcon className="w-3 h-3 inline mr-1" />{order.status}
                </span>
              </div>
              {order.notes && (
                <div className="bg-[#FFF3E0] rounded-xl px-3 py-2 mb-3 flex items-start gap-2" data-testid={`order-notes-${order.id}`}>
                  <StickyNote className="w-3.5 h-3.5 text-[#E16833] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[#6E5E52] italic">{order.notes}</p>
                </div>
              )}
              <div className="bg-[#F3EDE5] rounded-xl p-3 mb-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span>{item.quantity}x {item.name}</span>
                    <span className="text-[#6E5E52]">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
                {order.discount_amount > 0 && (
                  <div className="border-t border-[#E5DCD3] mt-2 pt-2 flex justify-between text-sm text-[#E63946]" data-testid={`order-discount-${order.id}`}>
                    <span>Remise ({order.discount_type === 'percent' ? `${order.discount_value}%` : `${order.discount_value} THB`})</span>
                    <span>-{formatPrice(order.discount_amount)}</span>
                  </div>
                )}
                <div className="border-t border-[#E5DCD3] mt-2 pt-2 flex justify-between font-bold">
                  <span>Total{order.discount_amount > 0 ? ' apres remise' : ''}</span>
                  <span className="text-[#E16833]">{formatPrice(afterDiscount)}</span>
                </div>
                {order.paid_amount > 0 && (
                  <div className="flex justify-between text-xs text-[#2D6A4F] mt-1">
                    <span>Deja paye</span><span>{formatPrice(order.paid_amount)}</span>
                  </div>
                )}
                {remaining > 0 && order.paid_amount > 0 && (
                  <div className="flex justify-between text-xs font-semibold text-[#E16833] mt-1">
                    <span>Reste a payer</span><span>{formatPrice(remaining)}</span>
                  </div>
                )}
                {order.payment_method && (
                  <div className="flex items-center gap-1 mt-1">
                    {order.payment_method === 'cash' && <Banknote className="w-3 h-3 text-[#2D6A4F]" />}
                    {order.payment_method === 'stripe' && <CreditCard className="w-3 h-3 text-[#6366F1]" />}
                    {order.payment_method === 'split' && <><Banknote className="w-3 h-3 text-[#2D6A4F]" /><CreditCard className="w-3 h-3 text-[#6366F1]" /></>}
                    <span className="text-[10px] text-[#6E5E52] uppercase">{order.payment_method}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {order.status !== 'served' && (
                  <select value={order.status} onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                    className="flex-1 h-10 rounded-xl border border-[#E5DCD3] bg-white px-3 text-sm min-w-[120px]"
                    data-testid={`status-select-${order.id}`}>
                    <option value="received">Recu</option>
                    <option value="preparing">En preparation</option>
                    <option value="ready">Pret</option>
                    <option value="served">Servi</option>
                  </select>
                )}
                <KitchenPrintButton orderId={order.id} tableId={order.table_id}
                  items={order.items} createdAt={order.created_at} notes={order.notes} />
                <Button onClick={() => onCashPayment(order, afterDiscount)}
                  className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl text-xs h-10 px-3"
                  data-testid={`cash-btn-${order.id}`}>
                  <Banknote className="w-4 h-4 mr-1" /> Cash
                </Button>
                <Button onClick={() => onCbPayment?.(order)}
                  className="bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-xl text-xs h-10 px-3"
                  data-testid={`cb-btn-${order.id}`}>
                  <CreditCard className="w-4 h-4 mr-1" /> CB
                </Button>
                <Button onClick={() => onOtherPayment?.(order, 'promptpay')}
                  className="bg-[#1B3A4B] hover:bg-[#142D3B] text-white rounded-xl text-xs h-10 px-3"
                  data-testid={`promptpay-btn-${order.id}`}>
                  <QrCode className="w-4 h-4 mr-1" /> PromptPay
                </Button>
                <Button onClick={() => onOtherPayment?.(order, 'counter')}
                  className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl text-xs h-10 px-3"
                  data-testid={`counter-btn-${order.id}`}>
                  <Building2 className="w-4 h-4 mr-1" /> Comptoir
                </Button>
                {!order.discount_amount && (
                  <Button onClick={() => onDiscount(order)} variant="outline"
                    className="rounded-xl border-[#D4A853] text-[#D4A853] hover:bg-[#D4A853] hover:text-white text-xs h-10 px-3"
                    data-testid={`discount-btn-${order.id}`}>
                    <Percent className="w-4 h-4 mr-1" /> Remise
                  </Button>
                )}
                <Button onClick={() => closeOrder(order.id)} variant="outline"
                  className="rounded-xl border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white h-10"
                  data-testid={`close-order-${order.id}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
        {orders.filter(o => !o.is_fully_paid).length === 0 && (
          <div className="col-span-full text-center py-12 text-[#6E5E52]">
            <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune commande active</p>
          </div>
        )}
      </div>
    </>
  );
};
